import React from "react";
import { Link } from "react-router-dom";
import { SITE_CONTENT } from "../../constants/siteContent";
import { trackCtaClick, trackNavigation } from "../../utils/analytics";
import {
  EXTERNAL_LINK_PROPS,
  mailUrl,
  phoneDisplay,
  telUrl,
  whatsappUrl
} from "../../utils/contact";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import Section from "./Section";
// Keep this import LAST: Footer.module.css must land after Section.module.css
// in the bundle (it re-points --section-pad-y on the panel).
import styles from "./Footer.module.css";

/*
 * Footer — the reference's closing composition (s14 / s15).
 *
 *   <footer>                 THE LIME ZONE: paper at its top edge fading into
 *                            full --lime. Everything below sits on lime.
 *     <Section tone="olive"  THE INSET OLIVE PANEL. It is the shared Section
 *              panel notch>  primitive rendered INSIDE the <footer> landmark, so
 *                            the radius, the 16px inset (8px <= 980px), the tab
 *                            notch, the contour layer, the on-olive text rules
 *                            and data-tone="olive" (the Header's signal) are the
 *                            same ones every other panel on the site uses. The
 *                            Section gets NO id: analyticsAutoTrack resolves a
 *                            click's source with closest("section[id]") first,
 *                            and footer clicks must keep reporting "footer".
 *     <div data-surface="lime">  the legal bar, ink on lime.
 *
 * The <h2> is outside <main>, so HeadingWipe never touches it — it is simply
 * static. It is never wrapped in Reveal.
 *
 * Contract: default export, no required props (onNavItemClick is optional),
 * every word read from SITE_CONTENT, UI labels in the TODO(content) consts.
 */

// TODO(content): UI labels. Every other word in the footer is read from
// SITE_CONTENT (finalCta, footer, techStack, ownProduct, contact).
const LABELS = {
  pages: "Pages",
  connect: "Connect",
  services: "Services",
  industries: "Industries",
  quietNav: "Services and industries",
  legalNav: "Legal",
  whatsapp: "WhatsApp",
  whatsappAria: "Chat on WhatsApp",
  call: "Call",
  email: "Email",
  cta: "Business enquiries",
  rights: "All rights reserved."
};

// TODO(content): which words of SITE_CONTENT.finalCta.title are set in the
// serif accent face (one to three words). Matching is case-insensitive; a word
// that is no longer in the title is simply not accented.
const STATEMENT_ACCENT_WORDS = ["product", "build"];

// TODO(content): supporting line under the office location.
const LOCATION_META = "Remote with clients across India, US, and UK";

const CTA_PATH = "/contact";
const PRODUCT_PATH = "/zeta";

/* The three Zeta documents are STATIC html in public/zeta/, served before the
   SPA (see public/_redirects) because Google Play's reviewer opens them with no
   bundle. They are plain anchors on purpose: a react-router <Link> would handle
   them client-side and land on the not-found route.
   TODO(content): labels — "{product}" is replaced by SITE_CONTENT.ownProduct.name. */
const PRODUCT_LEGAL = [
  { href: "/zeta/privacy-policy", label: "{product} privacy policy" },
  { href: "/zeta/terms-of-use", label: "{product} terms of use" },
  { href: "/zeta/delete-account", label: "Delete {product} account" }
];

/* The legal documents live in the same SITE_CONTENT list as the industry pages,
   but they are not industries — they belong in the bottom bar next to the
   copyright. Split them out here by path so siteContent.js stays untouched, and
   so a later `footer.legalLinks` array in SITE_CONTENT takes over cleanly. */
const LEGAL_PATHS = ["/privacy-policy", "/terms", "/terms-of-service", "/cookie-policy"];
const isLegalLink = (item) => LEGAL_PATHS.includes(item.path);

const PAGES_LABEL_ID = "footer-pages-label";
const CONNECT_LABEL_ID = "footer-connect-label";
const STACK_LABEL_ID = "footer-stack-label";

/* SITE_CONTENT.finalCta.title -> the mixed sans / serif lockup. The title is
   split on word boundaries, so punctuation stays in the sans face (the
   reference's "FIGHT" is serif, its full stop is not). Upper-casing is CSS. */
function renderStatement(title) {
  return String(title || "")
    .split(/([A-Za-z0-9]+)/)
    .filter(Boolean)
    .map((part, index) =>
      STATEMENT_ACCENT_WORDS.includes(part.toLowerCase()) ? (
        <em key={index}>{part}</em>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
}

function Footer({ onNavItemClick }) {
  const footer = SITE_CONTENT.footer || {};
  const finalCta = SITE_CONTENT.finalCta || {};
  const techStack = SITE_CONTENT.techStack || {};
  const product = SITE_CONTENT.ownProduct || {};

  const closeMenu = () => {
    if (typeof onNavItemClick === "function") {
      onNavItemClick();
    }
  };

  const handleLinkClick = (item, source) => {
    trackNavigation(item.label, item.path, source);
    closeMenu();
  };

  const ctaLabel = finalCta.button || LABELS.cta;
  const handleCtaClick = () => {
    trackCtaClick(ctaLabel, "footer", CTA_PATH);
    closeMenu();
  };

  /* ---- links ---- */
  const supportLinks = footer.industryLinks || footer.supportLinks || [];
  const industryLinks = supportLinks.filter((item) => !isLegalLink(item));
  const legalLinks = footer.legalLinks || supportLinks.filter(isLegalLink);
  const companyLinks = footer.companyLinks || [];

  const quietGroups = [
    { title: LABELS.services, links: footer.serviceLinks, source: "footer_services" },
    { title: LABELS.industries, links: industryLinks, source: "footer_industries" }
  ].filter((group) => group.links && group.links.length > 0);

  const productName = product.name || "";
  const productLegal = productName
    ? PRODUCT_LEGAL.map((item) => ({
        href: item.href,
        label: item.label.replace("{product}", productName)
      }))
    : [];

  /* ---- direct contact ----
     utils/contact is the single source for the number and the address; the
     older footer.phone / footer.email are only a fallback, so the footer can
     never show a channel the rest of the site does not. "" = render nothing. */
  const whatsappHref = whatsappUrl("default");
  const callHref =
    telUrl() || (footer.phone ? `tel:${String(footer.phone).replace(/[^\d+]/g, "")}` : "");
  const callLabel = phoneDisplay() || footer.phone || "";
  const mailHref = mailUrl() || (footer.email ? `mailto:${footer.email}` : "");
  const mailLabel = mailHref.replace(/^mailto:/, "");

  const connectLinks = [
    whatsappHref && {
      key: "whatsapp",
      href: whatsappHref,
      label: LABELS.whatsapp,
      ariaLabel: LABELS.whatsappAria,
      external: true
    },
    callHref &&
      callLabel && {
        key: "call",
        href: callHref,
        label: callLabel,
        ariaLabel: `${LABELS.call} ${callLabel}`
      },
    mailHref && {
      key: "email",
      href: mailHref,
      label: mailLabel,
      ariaLabel: `${LABELS.email} ${mailLabel}`
    }
  ].filter(Boolean);

  const locations = footer.locations || [];
  const stackItems = techStack.items || [];
  // The "A" monogram. There is no helmet photo and no signature asset, and
  // neither may be faked — the brand's own initial is the large centre mark.
  const monogram = String(SITE_CONTENT.brand || "").charAt(0).toUpperCase();

  return (
    <footer className={styles.footer}>
      <Section tone="olive" panel notch container="wide" className={styles.island}>
        <div className={styles.stage}>
          {monogram ? (
            <span className={styles.monogram} aria-hidden="true">
              {monogram}
            </span>
          ) : null}

          <div className={styles.lockup}>
            {finalCta.title ? (
              <Statement as="h2" size="statement" align="center">
                {renderStatement(finalCta.title)}
              </Statement>
            ) : null}
            {finalCta.subtitle ? <p className={styles.lead}>{finalCta.subtitle}</p> : null}
          </div>

          <div className={styles.columns}>
            <Reveal as="nav" className={styles.column} aria-labelledby={PAGES_LABEL_ID}>
              <MicroLabel as="p" id={PAGES_LABEL_ID} className={styles.columnLabel}>
                {LABELS.pages}
              </MicroLabel>
              <ul className={styles.linkList}>
                {companyLinks.map((item) => (
                  <li key={item.path}>
                    <Link
                      className={styles.bigLink}
                      to={item.path}
                      onClick={() => handleLinkClick(item, "footer_company")}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
                {productName ? (
                  <li className={styles.productItem}>
                    <Link
                      className={`${styles.bigLink} ${styles.productLink}`}
                      to={PRODUCT_PATH}
                      onClick={() =>
                        handleLinkClick({ label: productName, path: PRODUCT_PATH }, "footer_product")
                      }
                    >
                      {productName}
                    </Link>
                  </li>
                ) : null}
              </ul>
            </Reveal>

            <Reveal className={`${styles.column} ${styles.columnConnect}`} delay={120}>
              <MicroLabel as="p" id={CONNECT_LABEL_ID} className={styles.columnLabel}>
                {LABELS.connect}
              </MicroLabel>
              {connectLinks.length > 0 ? (
                <ul className={styles.linkList} aria-labelledby={CONNECT_LABEL_ID}>
                  {connectLinks.map((item) => (
                    <li key={item.key}>
                      <a
                        className={styles.bigLink}
                        href={item.href}
                        aria-label={item.ariaLabel}
                        {...(item.external ? EXTERNAL_LINK_PROPS : {})}
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}

              {locations.length > 0 ? (
                <div className={styles.location}>
                  {locations.map((location) => (
                    <p key={location} className={`micro ${styles.locationName}`}>
                      <Icon name="pin" size={16} />
                      {location}
                    </p>
                  ))}
                  <p className={styles.locationMeta}>{LOCATION_META}</p>
                </div>
              ) : null}
            </Reveal>
          </div>
        </div>

        {quietGroups.length > 0 ? (
          <Reveal as="nav" className={styles.quiet} aria-label={LABELS.quietNav}>
            {quietGroups.map((group, groupIndex) => {
              const groupLabelId = `footer-quiet-label-${groupIndex}`;
              return (
                <div key={group.title} className={styles.quietGroup}>
                  <MicroLabel id={groupLabelId} className={`micro--accent ${styles.quietLabel}`}>
                    {group.title}
                  </MicroLabel>
                  <ul className={styles.quietList} aria-labelledby={groupLabelId}>
                    {group.links.map((item) => (
                      <li key={item.path}>
                        <Link
                          className={`micro ${styles.quietLink}`}
                          to={item.path}
                          onClick={() => handleLinkClick(item, group.source)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </Reveal>
        ) : null}

        {stackItems.length > 0 ? (
          <Reveal className={styles.stack}>
            {techStack.title ? (
              <MicroLabel as="p" id={STACK_LABEL_ID} className={styles.stackLabel}>
                {techStack.title}
              </MicroLabel>
            ) : null}
            <ul
              className={styles.stackList}
              aria-labelledby={techStack.title ? STACK_LABEL_ID : undefined}
            >
              {stackItems.map((name) => (
                <li key={name} className={styles.stackItem}>
                  {name}
                </li>
              ))}
            </ul>
          </Reveal>
        ) : null}

        <div className={styles.ctaRow}>
          <Link className="btn" to={CTA_PATH} onClick={handleCtaClick}>
            {ctaLabel}
            <Icon name="arrow-up-right" size={18} />
          </Link>
        </div>
      </Section>

      <div className={styles.legal} data-surface="lime">
        <MicroLabel as="p" className={styles.copyright}>
          © {new Date().getFullYear()} {SITE_CONTENT.brand}. {LABELS.rights}
        </MicroLabel>

        {legalLinks.length > 0 || productLegal.length > 0 ? (
          <nav aria-label={LABELS.legalNav}>
            <ul className={styles.legalList}>
              {legalLinks.map((item) => (
                <li key={item.path}>
                  <Link
                    className={`micro ${styles.legalLink}`}
                    to={item.path}
                    onClick={() => handleLinkClick(item, "footer_legal")}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              {productLegal.map((item) => (
                <li key={item.href}>
                  {/* Static HTML outside the SPA — a plain anchor on purpose. */}
                  <a
                    className={`micro ${styles.legalLink}`}
                    href={item.href}
                    onClick={() => trackNavigation(item.label, item.href, "footer_legal")}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </div>
    </footer>
  );
}

export default Footer;
